import React from 'react';
import PropTypes from 'prop-types';

/**
 * ClaimErrorBoundary - A specialized error boundary for Claims-related components
 * 
 * This is a more focused error boundary specifically designed for the claims
 * management system. It provides claim-specific error messaging and recovery options.
 */
class ClaimErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    const errorId = `claim_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    this.logError(error, errorInfo);
    this.reportError(error, errorInfo);
  }

  logError = (error, errorInfo) => {
    const { errorId } = this.state;
    const { section = 'Claims Section' } = this.props;
    
    // Check if we're in development mode - avoid import.meta for Jest compatibility
    const isDev = window?.location?.hostname === 'localhost' || 
                  window?.location?.hostname === '127.0.0.1' ||
                  window?.location?.port === '5173'; // Vite dev server port
    
    if (isDev) {
      console.group(`🚨 Claims Error Boundary - ${section}`);
      console.error('Error ID:', errorId);
      console.error('Error:', error);
      console.error('Component Stack:', errorInfo?.componentStack);
      console.groupEnd();
    }
  };

  reportError = (error, errorInfo) => {
    const { errorId } = this.state;
    const { section = 'Claims Section' } = this.props;

    try {
      // Check if we're in production mode - avoid import.meta for Jest compatibility
      const isProd = window?.location?.protocol === 'https:' && 
                     !window?.location?.hostname?.includes('localhost') &&
                     !window?.location?.hostname?.includes('127.0.0.1');
      
      if (isProd) {
        fetch('/api/errors/claims', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            errorId,
            section,
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo?.componentStack,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            type: 'ui_error'
          })
        }).catch(() => {
          // Silent fail to prevent error loops
        });
      }
    } catch {
      // Silent fail
    }
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/dashboard';
    }
  };

  render() {
    if (this.state.hasError) {
      const { section = 'Claims', customMessage } = this.props;
      const { errorId, error } = this.state;

      return (
        <div className="min-h-[300px] flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-md border border-red-200 p-6 text-center">
            {/* Claims-specific error icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg 
                className="h-8 w-8 text-red-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 8v4m0 4h.01" 
                />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {section} Temporarily Unavailable
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              {customMessage || 
                `We're experiencing an issue with the ${section.toLowerCase()} system. This could be due to a temporary glitch.`
              }
            </p>

            <p className="text-xs text-gray-500 mb-6">
              Please try again or contact support if the problem persists.
            </p>

            {errorId && (
              <div className="bg-gray-50 rounded p-2 mb-4">
                <p className="text-xs text-gray-500">
                  Reference ID: <span className="font-mono">{errorId}</span>
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Try Again
              </button>
              
              <button
                onClick={this.handleGoBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Go Back
              </button>
            </div>

            {/* Development error details */}
            {window?.location?.hostname === 'localhost' && error && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-gray-500 cursor-pointer">
                  Error Details (Dev)
                </summary>
                <div className="mt-2 p-2 bg-red-50 rounded text-xs font-mono text-red-700 max-h-32 overflow-auto">
                  {error.message}
                </div>
              </details>
            )}

            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                Need immediate assistance? Call{' '}
                <a href="tel:+27860104555" className="text-blue-600 underline">
                  086 010 4555
                </a>
                {' '}or email{' '}
                <a href="mailto:claims@oldmutual.com" className="text-blue-600 underline">
                  claims@oldmutual.com
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ClaimErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  section: PropTypes.string,
  customMessage: PropTypes.string,
};

ClaimErrorBoundary.defaultProps = {
  section: 'Claims',
  customMessage: null,
};

export default ClaimErrorBoundary;
